import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { VideoSection } from '../components/VideoSection';
import { animationProjects, themes } from '../data/mock';

const Animation = () => {
  return (
    <div className="bg-black text-white">
      {/* Hero Section - No Video Background */}
      <section className="min-h-screen flex items-center justify-center px-6 lg:px-12 pt-20 relative overflow-hidden bg-black">
        <motion.div 
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <motion.h1 
            className="text-5xl md:text-7xl font-playfair font-normal tracking-tight mb-8 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Original animated storytelling
            <br />
            <span className="text-white/60">from Ruminating Room.</span>
          </motion.h1>
          <motion.p 
            className="text-xl text-white/60 font-light max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Narratives that explore the human condition through the lens of modern Indian life.
          </motion.p>
        </motion.div>
      </section>

      {/* Video Section - Full Brightness Below Hero */}
      <VideoSection 
        src="/videos/animation.mp4" 
        poster="/videos/animation-poster.jpg" 
        testId="animation-video-section" 
      />

      {/* Projects Showcase */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-playfair font-normal tracking-tight mb-16 text-center">
            Projects
          </h2>
          
          <div className="space-y-16">
            {animationProjects.map((project, index) => (
              <motion.div 
                key={project.id} 
                className="max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: index * 0.1 }}
              >
                <div className="space-y-6">
                  <div>
                    <div className="text-sm text-white/40 tracking-wider mb-2">{project.type}</div>
                    <h3 className="text-3xl md:text-4xl font-playfair font-normal tracking-tight mb-4">
                      {project.title}
                    </h3>
                  </div>
                  
                  <p className="text-white/60 text-lg leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {project.themes.map((theme, i) => (
                      <span 
                        key={i}
                        className="px-4 py-1.5 border border-white/20 text-sm tracking-wide text-white/60"
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Themes Section */}
      <section className="py-24 px-6 lg:px-12 bg-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-playfair font-normal tracking-tight mb-16 text-center">
            Themes
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {themes.map((theme) => (
              <Card key={theme.id} className="bg-black border-white/10">
                <CardContent className="p-8">
                  <h3 className="text-xl font-light tracking-tight mb-4 text-white">
                    {theme.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed">
                    {theme.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Collaboration CTA */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-playfair font-normal tracking-tight mb-6">
                Let's Collaborate
              </h2>
              <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
                Open to collaborations with producers, studios, and platforms interested in original animated content.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-white text-black hover:bg-white/90 text-base tracking-wide px-8 py-6"
                  onClick={() => window.location.href = 'mailto:contact.ruminatingroom@gmail.com'}
                >
                  Start a Conversation
                  <Mail className="ml-2" size={20} />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white hover:text-black text-base tracking-wide px-8 py-6"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  View Projects Again
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
              
              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="text-white/40 text-sm">
                  Interested in our fashion production services?{' '}
                  <a href="/" className="text-white hover:text-white/80 transition-colors underline">
                    Visit our main page
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Animation;
